import { useCallback, useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';
import { holdToast, releaseToast } from '../../lib/snoozeHold';

/**
 * SnoozeMenu — the duration picker, which now holds its notification open.
 * (notify-center, 7 Sep 2026)
 *
 * Replaces the inline menu in components/mail/MailNotifyBridge.jsx.  Three
 * things it does that the old one did not:
 *
 *  1. **It holds the card.**  `holdToast(notificationId)` on mount cancels the
 *     bridge's 12 s auto-dismiss; unmount calls `releaseToast` and restores it
 *     exactly as it was if nothing was picked, or gives a short confirmation
 *     window if something was.  The card cannot vanish mid-decision.
 *  2. **Keyboard.**  Focus lands on the first option, ↑/↓/Home/End move,
 *     Enter/Space pick, Tab cycles inside the menu, Escape closes it (and only
 *     it — the keydown is stopped so the panel behind stays open).
 *  3. **It is obvious what is being picked** — hover and focus both paint the
 *     row, with a visible ring for keyboard users.
 */

export const SNOOZE_PRESETS = [
  { id: '1h', label: 'In 1 hour', preset: '1h' },
  { id: 'evening', label: 'This evening (18:00)', preset: 'evening' },
  { id: 'tomorrow', label: 'Tomorrow (09:00)', preset: 'tomorrow' },
  { id: 'custom', label: 'Pick a time…', preset: null },
];

/** Hover AND focus are painted, so mouse and keyboard both show the target. */
const OPTION_CLASS =
  'w-full text-left px-2.5 py-1.5 rounded flex items-center gap-2 text-slate-700 ' +
  'transition-colors cursor-pointer hover:bg-slate-100 hover:text-slate-900 ' +
  'focus:outline-none focus:bg-slate-900 focus:text-white ' +
  'focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-1 ' +
  'data-[active=true]:bg-slate-900 data-[active=true]:text-white';

export default function SnoozeMenu({
  notificationId,
  onPick,
  onClose,
  align = 'right',
}) {
  const [custom, setCustom] = useState(false);
  const [when, setWhen] = useState('');
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);
  const pickedRef = useRef(false);

  // --- the hold ---------------------------------------------------------
  useEffect(() => {
    if (notificationId === undefined || notificationId === null) return undefined;
    holdToast(notificationId);
    return () => {
      // Picked -> leave the confirmation up for a moment, then let it go.
      // Closed without picking -> the original timer resumes untouched.
      releaseToast(notificationId, pickedRef.current ? { restartMs: 3200 } : {});
    };
  }, [notificationId]);

  const choose = useCallback((choice) => {
    pickedRef.current = true;
    if (onPick) onPick(choice);
  }, [onPick]);

  // --- focus ------------------------------------------------------------
  const options = useCallback(
    () => (boxRef.current
      ? Array.from(boxRef.current.querySelectorAll('[data-snooze-option]'))
      : []),
    [],
  );

  useEffect(() => {
    const list = options();
    if (list.length) {
      setActive(0);
      try { list[0].focus(); } catch (e) { /* jsdom without focus */ }
    }
  }, [custom, options]);

  const onKeyDown = (e) => {
    const list = options();
    if (e.key === 'Escape') {
      // Close the menu, not the panel behind it.
      e.preventDefault();
      e.stopPropagation();
      if (onClose) onClose();
      return;
    }
    if (!list.length) return;
    const focusAt = (i) => {
      const next = (i + list.length) % list.length;
      setActive(next);
      try { list[next].focus(); } catch (err) { /* jsdom */ }
    };
    const here = list.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); focusAt((here < 0 ? -1 : here) + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); focusAt((here < 0 ? 1 : here) - 1); }
    else if (e.key === 'Home') { e.preventDefault(); focusAt(0); }
    else if (e.key === 'End') { e.preventDefault(); focusAt(list.length - 1); }
    else if (e.key === 'Tab') {
      // Keep the keyboard inside the menu while it is open.
      e.preventDefault();
      focusAt((here < 0 ? -1 : here) + (e.shiftKey ? -1 : 1));
    }
  };

  return (
    <div
      ref={boxRef}
      role="menu"
      aria-label="Snooze this notification"
      className={`absolute z-[80] mt-1 w-56 rounded-lg border border-slate-200 bg-white shadow-xl p-1 text-sm ${align === 'right' ? 'right-0' : 'left-0'}`}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={onKeyDown}
      data-testid="snooze-menu"
    >
      {!custom ? SNOOZE_PRESETS.map((p, i) => (
        <button
          key={p.id}
          type="button"
          role="menuitem"
          data-snooze-option
          data-active={active === i}
          data-testid={`snooze-option-${p.id}`}
          className={OPTION_CLASS}
          onMouseEnter={() => setActive(i)}
          onClick={() => (p.preset
            ? choose({ preset: p.preset, label: p.label })
            : setCustom(true))}
        >
          <Clock className="w-3.5 h-3.5 opacity-70" /> {p.label}
        </button>
      )) : (
        <div className="p-2 space-y-2">
          <label className="block text-xs text-slate-500" htmlFor="snooze-when">
            Remind me at (IST)
          </label>
          <input
            id="snooze-when"
            type="datetime-local"
            value={when}
            data-snooze-option
            onChange={(e) => setWhen(e.target.value)}
            className="w-full border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-slate-900"
          />
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              data-snooze-option
              className="text-xs text-slate-500 rounded px-1 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400"
              onClick={() => setCustom(false)}
            >
              Back
            </button>
            <button
              type="button"
              data-snooze-option
              data-testid="snooze-option-custom-confirm"
              className="text-xs px-2 py-1 rounded bg-slate-900 text-white disabled:opacity-40 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-1"
              disabled={!when}
              onClick={() => choose({ until: when, label: `at ${when.replace('T', ' ')}` })}
            >
              Snooze
            </button>
          </div>
        </div>
      )}
      <button
        type="button"
        role="menuitem"
        data-snooze-option
        data-testid="snooze-cancel"
        className="w-full text-left px-2.5 py-1 text-xs text-slate-400 rounded hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:bg-slate-100 focus:text-slate-700"
        onClick={onClose}
      >
        Cancel
      </button>
    </div>
  );
}

export { SnoozeMenu };
